import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportPanel = ({ isOpen = false, onClose }) => {
  const [exportConfig, setExportConfig] = useState({
    format: 'pdf',
    includeCharts: true,
    includeData: true,
    includeAnalysis: true,
    dateRange: 'current',
    schedule: 'none'
  });

  const [selectedSections, setSelectedSections] = useState({
    summary: true,
    distribution: true,
    warranty: true,
    ageAnalysis: true,
    statusCards: true,
    recommendations: true
  });

  const formatOptions = [
    { value: 'pdf', label: 'PDF Report' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV Data' },
    { value: 'powerpoint', label: 'PowerPoint Presentation' }
  ];

  const dateRangeOptions = [
    { value: 'current', label: 'Current View' },
    { value: '3months', label: 'Last 3 Months' },
    { value: '6months', label: 'Last 6 Months' },
    { value: '12months', label: 'Last 12 Months' },
    { value: 'ytd', label: 'Year to Date' }
  ];

  const scheduleOptions = [
    { value: 'none', label: 'One-time Export' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const exportTemplates = [
    {
      id: 'executive',
      name: 'Executive Summary',
      description: 'High-level overview for stakeholders',
      sections: ['summary', 'distribution', 'recommendations'],
      format: 'pdf'
    },
    {
      id: 'detailed',
      name: 'Detailed Analysis',
      description: 'Comprehensive report with all data',
      sections: ['summary', 'distribution', 'warranty', 'ageAnalysis', 'statusCards', 'recommendations'],
      format: 'pdf'
    },
    {
      id: 'data-export',
      name: 'Data Export',
      description: 'Raw data for further analysis',
      sections: ['distribution', 'warranty', 'ageAnalysis', 'statusCards'],
      format: 'excel'
    },
    {
      id: 'presentation',
      name: 'Presentation Ready',
      description: 'Charts and visuals for meetings',
      sections: ['summary', 'distribution', 'warranty', 'ageAnalysis'],
      format: 'powerpoint'
    }
  ];

  const handleConfigChange = (key, value) => {
    setExportConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSectionToggle = (section) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleTemplateSelect = (template) => {
    const newSelectedSections = {};
    Object.keys(selectedSections)?.forEach(key => {
      newSelectedSections[key] = template?.sections?.includes(key);
    });
    setSelectedSections(newSelectedSections);
    setExportConfig(prev => ({ ...prev, format: template?.format }));
  };

  const handleExport = () => {
    const exportData = {
      ...exportConfig,
      sections: selectedSections,
      timestamp: new Date()?.toISOString(),
      filename: `asset-analytics-${exportConfig?.format}-${Date.now()}`
    };
    
    console.log('Exporting with config:', exportData);
    
    // Simulate export process
    setTimeout(() => {
      alert(`Export completed! File: ${exportData?.filename}.${exportConfig?.format}`);
      onClose?.();
    }, 2000);
  };

  const getSelectedSectionsCount = () => {
    return Object.values(selectedSections)?.filter(Boolean)?.length;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:relative lg:inset-auto">
      {/* Mobile Overlay */}
      <div 
        className="fixed inset-0 bg-black/20 lg:hidden" 
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-card border-l border-border shadow-elevated lg:absolute lg:top-0 lg:right-0 lg:w-96 lg:max-h-[600px] lg:rounded-lg lg:border lg:shadow-elevated overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-card-foreground">Export Analytics</h3>
              <p className="text-sm text-muted-foreground">Generate comprehensive reports</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick Templates */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3">Quick Templates</h4>
            <div className="grid grid-cols-1 gap-3">
              {exportTemplates?.map((template) => (
                <button
                  key={template?.id}
                  onClick={() => handleTemplateSelect(template)}
                  className="p-3 text-left border border-border rounded-lg hover:border-primary/50 hover:bg-muted/50 transition-smooth"
                >
                  <div className="font-medium text-card-foreground text-sm mb-1">
                    {template?.name}
                  </div>
                  <div className="text-xs text-muted-foreground mb-2">
                    {template?.description}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                      {template?.format?.toUpperCase()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {template?.sections?.length} sections
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Export Format */}
          <div>
            <Select
              label="Export Format"
              options={formatOptions}
              value={exportConfig?.format}
              onChange={(value) => handleConfigChange('format', value)}
            />
          </div>

          {/* Date Range */}
          <div>
            <Select
              label="Date Range"
              options={dateRangeOptions}
              value={exportConfig?.dateRange}
              onChange={(value) => handleConfigChange('dateRange', value)}
            />
          </div>

          {/* Content Sections */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-card-foreground">Include Sections</h4>
              <span className="text-sm text-muted-foreground">
                {getSelectedSectionsCount()} selected
              </span>
            </div>
            
            <div className="space-y-3">
              <Checkbox
                label="Executive Summary"
                description="Key metrics and insights overview"
                checked={selectedSections?.summary}
                onChange={() => handleSectionToggle('summary')}
              />
              
              <Checkbox
                label="Asset Distribution Charts"
                description="Department and category breakdowns"
                checked={selectedSections?.distribution}
                onChange={() => handleSectionToggle('distribution')}
              />
              
              <Checkbox
                label="Warranty Timeline"
                description="Expiration tracking and projections"
                checked={selectedSections?.warranty}
                onChange={() => handleSectionToggle('warranty')}
              />
              
              <Checkbox
                label="Age Analysis"
                description="Asset lifecycle and replacement planning"
                checked={selectedSections?.ageAnalysis}
                onChange={() => handleSectionToggle('ageAnalysis')}
              />
              
              <Checkbox
                label="Status Summary"
                description="Current asset status breakdown"
                checked={selectedSections?.statusCards}
                onChange={() => handleSectionToggle('statusCards')}
              />
              
              <Checkbox
                label="Recommendations"
                description="Actionable insights and next steps"
                checked={selectedSections?.recommendations}
                onChange={() => handleSectionToggle('recommendations')}
              />
            </div>
          </div>

          {/* Additional Options */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3">Additional Options</h4>
            <div className="space-y-3">
              <Checkbox
                label="Include Charts & Visualizations"
                checked={exportConfig?.includeCharts}
                onChange={() => handleConfigChange('includeCharts', !exportConfig?.includeCharts)}
              />
              
              <Checkbox
                label="Include Raw Data Tables"
                checked={exportConfig?.includeData}
                onChange={() => handleConfigChange('includeData', !exportConfig?.includeData)}
              />
              
              <Checkbox
                label="Include Analysis & Commentary"
                checked={exportConfig?.includeAnalysis}
                onChange={() => handleConfigChange('includeAnalysis', !exportConfig?.includeAnalysis)}
              />
            </div>
          </div>

          {/* Scheduling */}
          <div>
            <Select
              label="Schedule Export"
              description="Set up recurring reports"
              options={scheduleOptions}
              value={exportConfig?.schedule}
              onChange={(value) => handleConfigChange('schedule', value)}
            />
          </div>

          {/* Export Preview */}
          <div className="p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="FileText" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-card-foreground">Export Preview</span>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <div>Format: {exportConfig?.format?.toUpperCase()}</div>
              <div>Sections: {getSelectedSectionsCount()}</div>
              <div>Date Range: {dateRangeOptions?.find(opt => opt?.value === exportConfig?.dateRange)?.label}</div>
              <div>Generated: {new Date()?.toLocaleDateString()}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={getSelectedSectionsCount() === 0}
              className="flex-1"
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;